import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getAllStudents,
  getAllParents,
  getAllFaculty
} from '../services/rosterService';

/**
 * Custom hook that loads roster data from Firebase and builds a SCHOOL_ROSTER-compatible object
 * with all the utility functions the app expects.
 * Data is scoped to the current authenticated user.
 */
export function useFirebaseRoster() {
  const { currentUser } = useAuth();
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [faculty, setFaculty] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRoster = useCallback(async () => {
    if (!currentUser?.uid) {
      setLoading(false);
      setError('No user logged in');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [studentsData, parentsData, facultyData] = await Promise.all([
        getAllStudents(currentUser.uid),
        getAllParents(currentUser.uid),
        getAllFaculty(currentUser.uid)
      ]);
      setStudents(studentsData);
      setParents(parentsData);
      setFaculty(facultyData);
    } catch (err) {
      console.error('Error fetching roster:', err);
      setError('Failed to load roster data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  useEffect(() => {
    fetchRoster();
  }, [fetchRoster]);

  // Build the SCHOOL_ROSTER object with utility functions
  const SCHOOL_ROSTER = useMemo(() => {
    // Transform Firebase student data to match expected format
    // Firebase uses 'name', local file used 'student'
    const transformedStudents = students.map(s => ({
      student: s.name,
      teacher: s.teacher || '',
      grade: s.grade || '',
      barcode: s.barcode || '',
      pin: s.pin || ''
    }));

    return {
      students: transformedStudents,
      parents: parents,
      faculty: faculty,

      // Find teacher by student name (case-insensitive)
      getTeacher: function(studentName) {
        if (!studentName) return null;

        const cleanName = studentName.trim();

        // Try exact match first
        let student = this.students.find(s =>
          s.student.toLowerCase() === cleanName.toLowerCase()
        );

        // If no exact match, try last name, first name pattern
        if (!student) {
          const parts = cleanName.split(',').map(p => p.trim());
          if (parts.length === 2) {
            const reversed = `${parts[1]}, ${parts[0]}`;
            student = this.students.find(s =>
              s.student.toLowerCase() === reversed.toLowerCase()
            );
          }

          // Try partial match on last name
          if (!student) {
            student = this.students.find(s =>
              s.student.toLowerCase().startsWith(cleanName.toLowerCase())
            );
          }
        }

        return student ? {
          teacher: student.teacher,
          grade: student.grade,
          fullMatch: true
        } : null;
      },

      // Get all students for a specific teacher
      getStudentsByTeacher: function(teacherName) {
        if (!teacherName) return [];
        return this.students.filter(s =>
          s.teacher.toLowerCase() === teacherName.toLowerCase()
        );
      },

      // Get all students by grade
      getStudentsByGrade: function(grade) {
        if (!grade) return [];
        const normalizedGrade = grade.toString().toUpperCase();
        return this.students.filter(s =>
          s.grade.toUpperCase() === normalizedGrade
        );
      },

      // Check if student exists
      hasStudent: function(studentName) {
        return !!this.getTeacher(studentName);
      },

      // Get student details
      getStudent: function(studentName) {
        if (!studentName) return null;
        const cleanName = studentName.trim();
        return this.students.find(s =>
          s.student.toLowerCase() === cleanName.toLowerCase()
        ) || null;
      },

      // Search students (partial match)
      searchStudents: function(searchTerm) {
        if (!searchTerm) return [];
        const term = searchTerm.toLowerCase();
        return this.students.filter(s =>
          s.student.toLowerCase().includes(term) ||
          s.teacher.toLowerCase().includes(term) ||
          s.grade.toLowerCase().includes(term)
        );
      },

      // Get all unique teachers
      getAllTeachers: function() {
        const teachers = [...new Set(this.students.map(s => s.teacher).filter(Boolean))];
        return teachers.sort();
      },

      // Get all unique grades
      getAllGrades: function() {
        const gradeOrder = ['PK3', 'PK4', 'K', '1', '2', '3', '4', '5', '6', '7', '8', 'MS'];
        const grades = [...new Set(this.students.map(s => s.grade).filter(Boolean))];
        return grades.sort((a, b) => {
          const indexA = gradeOrder.indexOf(a);
          const indexB = gradeOrder.indexOf(b);
          if (indexA === -1 && indexB === -1) return a.localeCompare(b);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
      },

      // Get parent information (flexible matching)
      getParent: function(parentName) {
        if (!parentName) return null;

        let cleanName = parentName.trim();

        // Try exact match first
        let parent = this.parents.find(p =>
          p.name.toLowerCase() === cleanName.toLowerCase()
        );

        if (parent) return parent;

        // Handle "LastName, FirstName MiddleName" to "MiddleName LastName, FirstName"
        const parts = cleanName.split(',').map(p => p.trim());
        if (parts.length === 2) {
          const nameParts = parts[1].split(' ').map(p => p.trim()).filter(p => p);
          if (nameParts.length > 1) {
            const firstNames = nameParts.slice(0, -1).join(' ');
            const lastWord = nameParts[nameParts.length - 1];
            const altName = `${lastWord} ${parts[0]}, ${firstNames}`;
            parent = this.parents.find(p =>
              p.name.toLowerCase() === altName.toLowerCase()
            );
          }
        }

        return parent || null;
      },

      // Get faculty information (flexible matching)
      getFaculty: function(facultyName) {
        if (!facultyName) return null;

        let cleanName = facultyName.trim();

        // Try exact match first
        let facultyMember = this.faculty.find(f =>
          f.name.toLowerCase() === cleanName.toLowerCase()
        );

        if (facultyMember) return facultyMember;

        // Handle "Sr., Name" -> "Sr. Name" (remove comma after Sr.)
        if (cleanName.toLowerCase().startsWith('sr.,')) {
          cleanName = cleanName.replace(/^sr\.,\s*/i, 'Sr. ');
          facultyMember = this.faculty.find(f =>
            f.name.toLowerCase() === cleanName.toLowerCase()
          );
        }

        return facultyMember || null;
      },

      // Determine patron type
      getPatronType: function(name) {
        if (this.getParent(name)) return 'parent';
        if (this.getFaculty(name)) return 'faculty';
        if (this.getStudent(name)) return 'student';
        return 'unknown';
      },

      // Get statistics
      getStats: function() {
        const stats = {
          totalStudents: this.students.length,
          totalParents: this.parents.length,
          totalFaculty: this.faculty.length,
          byGrade: {},
          byTeacher: {},
          grades: this.getAllGrades(),
          teachers: this.getAllTeachers()
        };

        this.students.forEach(s => {
          if (s.grade) stats.byGrade[s.grade] = (stats.byGrade[s.grade] || 0) + 1;
          if (s.teacher) stats.byTeacher[s.teacher] = (stats.byTeacher[s.teacher] || 0) + 1;
        });

        return stats;
      }
    };
  }, [students, parents, faculty]);

  return {
    SCHOOL_ROSTER,
    loading,
    error,
    refresh: fetchRoster,
    userId: currentUser?.uid
  };
}

export default useFirebaseRoster;
